import axios from "axios";
import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  PersonCheck,
  ShieldCheck,
  Archive,
  ArrowCounterclockwise,
  Trash,
} from "react-bootstrap-icons";

import { ApiConnectionString } from "../../constants/ApiConntection";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuthContext } from "../../hooks/useAuthContext";
import PrivacyPinModal from "../../modals/PrivacyPinModal";
import "./EditClientPage.css";

export default function EditClientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const unlockSuccessfulRef = useRef(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [email, setEmail] = useState("");

  const [clientPortalEnabled, setClientPortalEnabled] = useState(false);
  const [canSelfBookAppointments, setCanSelfBookAppointments] =
    useState(false);

  const [emailVerified, setEmailVerified] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [originalClientPortalEnabled, setOriginalClientPortalEnabled] =
    useState(false);

  const [
    originalCanSelfBookAppointments,
    setOriginalCanSelfBookAppointments,
  ] = useState(false);

  const [privacyUnlocked, setPrivacyUnlocked] = useState(false);
  const [showPrivacyPinModal, setShowPrivacyPinModal] = useState(false);

  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [complete, setComplete] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [err, setErr] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const normalisedEmail = email.trim().toLowerCase();

  const portalTurnedOn =
    !originalClientPortalEnabled &&
    clientPortalEnabled;

  const getClient = async () => {
    setIsLoading(true);
    setErr("");
    setSuccessMessage("");

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/client/${id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const client = response.data;

      const currentEmail =
        client.email ||
        client.Email ||
        "";

      const currentClientPortalEnabled =
        client.clientPortalEnabled ??
        client.ClientPortalEnabled ??
        false;

      const currentCanSelfBook =
        client.canSelfBookAppointments ??
        client.CanSelfBookAppointments ??
        false;

      setFirstName(
        client.firstName ||
        client.FirstName ||
        ""
      );

      setLastName(
        client.lastName ||
        client.LastName ||
        ""
      );

      setEmail(currentEmail);

      setClientPortalEnabled(
        currentClientPortalEnabled
      );

      setOriginalClientPortalEnabled(
        currentClientPortalEnabled
      );

      setCanSelfBookAppointments(
        currentClientPortalEnabled
          ? currentCanSelfBook
          : false
      );

      setOriginalCanSelfBookAppointments(
        currentClientPortalEnabled
          ? currentCanSelfBook
          : false
      );

      setEmailVerified(
        client.emailVerified ??
        client.EmailVerified ??
        false
      );

      setIsActive(
        client.isActive ??
        client.IsActive ??
        true
      );
    } catch (error) {
      console.error(error);
      setErr("Failed to load client.");
    } finally {
      setIsLoading(false);
    }
  };

  const checkPrivacyAccess = async () => {
    if (!user?.token) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/privacy-pin/status`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const data = response.data || {};

      const unlocked =
        data.unlocked ??
        data.Unlocked ??
        data.isUnlocked ??
        data.IsUnlocked ??
        false;

      if (unlocked) {
        setPrivacyUnlocked(true);
        await getClient();
        return;
      }

      setPrivacyUnlocked(false);
      setShowPrivacyPinModal(true);
      setIsLoading(false);
    } catch (error) {
      console.error(
        "Failed to check privacy PIN status:",
        error
      );

      setErr("Failed to check privacy access.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token && id) {
      checkPrivacyAccess();
    }
  }, [user?.token, id]);

  const handlePrivacyUnlocked = async () => {
    unlockSuccessfulRef.current = true;

    setPrivacyUnlocked(true);
    setShowPrivacyPinModal(false);

    await getClient();
  };

  const handlePrivacyModalClose = () => {
    setShowPrivacyPinModal(false);

    if (!unlockSuccessfulRef.current) {
      navigate(`/client/${id}`);
      return;
    }

    unlockSuccessfulRef.current = false;
  };

  const getErrorMessage = (error, fallback) => {
    if (typeof error?.response?.data === "string") {
      return error.response.data;
    }

    if (error?.response?.data?.message) {
      return error.response.data.message;
    }

    return fallback;
  };

  const submit = async (e) => {
    e.preventDefault();

    setValidated(true);
    setErr("");
    setSuccessMessage("");

    if (!firstName.trim()) {
      setErr("First name is required.");
      return;
    }

    if (!lastName.trim()) {
      setErr("Last name is required.");
      return;
    }

    if (
      clientPortalEnabled &&
      !normalisedEmail
    ) {
      setErr(
        "This client does not have an email address, so client portal access cannot be enabled."
      );

      return;
    }

    setIsSaving(true);

    try {
      const updateClient = {
        FirstName: firstName.trim(),
        LastName: lastName.trim(),
        Email: normalisedEmail
          ? normalisedEmail
          : null,
        ClientPortalEnabled:
          clientPortalEnabled,
        CanSelfBookAppointments:
          clientPortalEnabled
            ? canSelfBookAppointments
            : false,
        IsActive: isActive,
      };

      const response = await axios.put(
        `${ApiConnectionString}/api/client/${id}`,
        updateClient,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setEmailSent(
        response.data?.emailSent || false
      );

      setComplete(true);
    } catch (error) {
      console.error(error);

      setErr(
        getErrorMessage(
          error,
          "Failed to update client."
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const archiveClient = async () => {
    const confirmed = window.confirm(
      "Archive this client? Portal access and self-booking will be turned off."
    );

    if (!confirmed) return;

    setIsSaving(true);
    setErr("");
    setSuccessMessage("");

    try {
      await axios.put(
        `${ApiConnectionString}/api/client/${id}/archive`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setIsActive(false);
      setClientPortalEnabled(false);
      setOriginalClientPortalEnabled(false);
      setCanSelfBookAppointments(false);
      setOriginalCanSelfBookAppointments(false);
      setEmailVerified(false);

      setSuccessMessage("Client archived.");
    } catch (error) {
      console.error(error);

      setErr(
        getErrorMessage(
          error,
          "Failed to archive client."
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const restoreClient = async () => {
    const confirmed =
      window.confirm("Restore this client?");

    if (!confirmed) return;

    setIsSaving(true);
    setErr("");
    setSuccessMessage("");

    try {
      await axios.put(
        `${ApiConnectionString}/api/client/${id}/restore`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setIsActive(true);
      setSuccessMessage("Client restored.");
    } catch (error) {
      console.error(error);

      setErr(
        getErrorMessage(
          error,
          "Failed to restore client."
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteClient = async () => {
    const confirmed = window.confirm(
      "Delete this client? This cannot be undone."
    );

    if (!confirmed) return;

    setIsSaving(true);
    setErr("");
    setSuccessMessage("");

    try {
      await axios.delete(
        `${ApiConnectionString}/api/client/${id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      navigate("/clients");
    } catch (error) {
      console.error(error);

      setErr(
        getErrorMessage(
          error,
          "Failed to delete client."
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!privacyUnlocked) {
    return (
      <>
        <PrivacyPinModal
          show={showPrivacyPinModal}
          token={user?.token}
          onClose={handlePrivacyModalClose}
          onUnlocked={handlePrivacyUnlocked}
        />

        <div className="edit-client-loading">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (isLoading || isSaving) {
    return (
      <div className="edit-client-loading">
        <LoadingSpinner />
      </div>
    );
  }

  if (complete) {
    return (
      <div className="edit-client-page">
        <div className="edit-client-card success-card">
          <div className="edit-client-icon success-icon">
            <ShieldCheck />
          </div>

          <h1>Client updated</h1>

          <p>
            <strong>
              {firstName} {lastName}
            </strong>{" "}
            has been updated successfully.
          </p>

          {emailSent && (
            <div className="edit-client-warning">
              <ShieldCheck />

              <p>
                A new client portal invite has been sent.
                The client will need to verify their email
                and create a password.
              </p>
            </div>
          )}

          {clientPortalEnabled &&
            !emailVerified &&
            !emailSent && (
              <div className="edit-client-warning">
                <ShieldCheck />

                <p>
                  Client portal access is enabled, but this
                  client still needs to verify their email
                  before logging in.
                </p>
              </div>
            )}

          <button
            className="btn edit-client-btn w-100"
            onClick={() =>
              navigate(`/client/${id}`)
            }
          >
            Back to client
          </button>

          <button
            className="btn edit-client-secondary-btn w-100"
            onClick={() => {
              setComplete(false);

              setOriginalClientPortalEnabled(
                clientPortalEnabled
              );

              setOriginalCanSelfBookAppointments(
                canSelfBookAppointments
              );

              setValidated(false);
            }}
          >
            Continue editing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-client-page">
      <div className="edit-client-top">
        <Link
          to={`/client/${id}`}
          className="edit-client-back"
        >
          <ArrowLeft /> Back to client
        </Link>
      </div>

      <div className="edit-client-card">
        <div className="edit-client-header">
          <div className="edit-client-icon">
            <PersonCheck />
          </div>

          <div>
            <h1>Edit client</h1>
            <p>Update client details and access.</p>
          </div>
        </div>

        {err && (
          <div className="alert alert-danger">
            {err}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
          </div>
        )}

        {!isActive && (
          <div className="edit-client-archived-notice">
            <Archive />

            <p>
              This client is archived. Restore them
              to edit access.
            </p>
          </div>
        )}

        <form
          className={
            validated
              ? "was-validated"
              : ""
          }
          onSubmit={submit}
          noValidate
        >
          <section className="edit-client-section">
            <h2>Client details</h2>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  First name
                </label>

                <input
                  className={`form-control edit-client-input ${
                    validated &&
                    !firstName.trim()
                      ? "is-invalid"
                      : ""
                  }`}
                  value={firstName}
                  onChange={(e) =>
                    setFirstName(
                      e.target.value
                    )
                  }
                />

                <div className="invalid-feedback">
                  Enter first name
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Last name
                </label>

                <input
                  className={`form-control edit-client-input ${
                    validated &&
                    !lastName.trim()
                      ? "is-invalid"
                      : ""
                  }`}
                  value={lastName}
                  onChange={(e) =>
                    setLastName(
                      e.target.value
                    )
                  }
                />

                <div className="invalid-feedback">
                  Enter last name
                </div>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">
                Email address
              </label>

              <input
                type="email"
                className="form-control edit-client-input"
                value={
                  email ||
                  "No email saved"
                }
                disabled
              />

              <div className="edit-client-small-note">
                Email address cannot be edited here.
              </div>
            </div>
          </section>

          <section className="edit-client-section">
            <h2>Access</h2>

            <label className="permission-card">
              <input
                type="checkbox"
                checked={
                  clientPortalEnabled
                }
                disabled={!isActive}
                onChange={(e) => {
                  const enabled =
                    e.target.checked;

                  if (
                    enabled &&
                    !normalisedEmail
                  ) {
                    setErr(
                      "This client does not have an email address, so client portal access cannot be enabled."
                    );

                    return;
                  }

                  setErr("");

                  setClientPortalEnabled(
                    enabled
                  );

                  if (!enabled) {
                    setCanSelfBookAppointments(
                      false
                    );
                  }
                }}
              />

              <div>
                <strong>
                  Enable client portal
                </strong>

                <span>
                  Client can log in and view
                  appointments.
                </span>
              </div>
            </label>

            {clientPortalEnabled && (
              <>
                <label className="permission-card">
                  <input
                    type="checkbox"
                    checked={
                      canSelfBookAppointments
                    }
                    disabled={!isActive}
                    onChange={(e) =>
                      setCanSelfBookAppointments(
                        e.target.checked
                      )
                    }
                  />

                  <div>
                    <strong>
                      Allow self-booking
                    </strong>

                    <span>
                      Client can book available
                      appointment times.
                    </span>
                  </div>
                </label>

                {!emailVerified &&
                  !portalTurnedOn && (
                    <div className="edit-client-warning">
                      <ShieldCheck />

                      <p>
                        This client still needs to
                        verify their email before
                        they can log in.
                      </p>
                    </div>
                  )}

                {portalTurnedOn && (
                  <div className="edit-client-warning">
                    <ShieldCheck />

                    <p>
                      Saving will send a client
                      portal invite.
                    </p>
                  </div>
                )}
              </>
            )}
          </section>

          <button
            type="submit"
            className="btn edit-client-btn w-100"
          >
            Save changes
          </button>

          <section className="edit-client-section edit-client-danger-zone">
            <h2>Archive or delete</h2>

            <div className="edit-client-danger-card">
              <div>
                <strong>
                  {isActive
                    ? "Archive client"
                    : "Restore client"}
                </strong>

                <p>
                  {isActive
                    ? "Archive keeps the client saved but turns off portal access."
                    : "Restore makes this client active again."}
                </p>
              </div>

              {isActive ? (
                <button
                  type="button"
                  className="btn edit-client-archive-btn"
                  onClick={archiveClient}
                >
                  <Archive /> Archive
                </button>
              ) : (
                <button
                  type="button"
                  className="btn edit-client-restore-btn"
                  onClick={restoreClient}
                >
                  <ArrowCounterclockwise /> Restore
                </button>
              )}
            </div>

            <div className="edit-client-danger-card delete-card">
              <div>
                <strong>
                  Delete client
                </strong>

                <p>
                  Only delete a client if they
                  have no appointment history.
                </p>
              </div>

              <button
                type="button"
                className="btn edit-client-delete-btn"
                onClick={deleteClient}
              >
                <Trash /> Delete
              </button>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
}